import React, { useCallback, useState, useMemo, useEffect, useLayoutEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { selectBestPosition } from '../../../utils/tooltipPositioning';
import { formatPLN } from '@repo/domain';

// pixels of horizontal drag per one frame step in 360° mode
const DRAG_PX_360 = 40;

/**
 * PropertySVGOverlayV2 - Navigator V2 format renderer
 * Renders shapes from navigator_config_v2 format (points array instead of svgPath string)
 * Supports navigation between artboards via navigateTo attribute
 * When navigatorConfig.is360 === true: drag-to-rotate mode (360° panorama)
 * Colors shapes based on property status from database
 */

// Format area with m² unit
const formatArea = (value) => {
  if (!value && value !== 0) return null;
  return `${value.toFixed(2).replace('.', ',')} m²`;
};

// Format price per m²
const formatPricePerM2 = (price, area) => {
  if (!price || !area || area === 0) return null;
  const pricePerM2 = price / area;
  return formatPLN(pricePerM2);
};

// Status color mapping
const STATUS_COLORS = {
  available: '#a5ce8e',
  reserved: '#F57C00',
  sold: '#D32F2F',
  coming_soon: '#BDBDBD'
};

const STATUS_BORDER_COLORS = {
  available: '#429c0b',
  reserved: '#BF360C',
  sold: '#7F0000',
  coming_soon: '#424242'
};

const STATUS_OPACITY = {
  available:   { normal: 0.65, hover: 0.95 },
  reserved:    { normal: 0.78, hover: 1.0  },
  sold:        { normal: 0.65, hover: 0.95 },
  coming_soon: { normal: 0.65, hover: 0.95 }
};

// Default style for shapes without matching property (navigation shapes etc.)
// No fill by default, white only on hover
const DEFAULT_FILL = 'transparent';
const DEFAULT_FILL_OPACITY = 0;
const DEFAULT_HOVER_FILL = '#ffffff';
const DEFAULT_HOVER_FILL_OPACITY = 0.3;

// Convert points array to SVG path string
const pointsToPath = (points) => {
  if (!points || points.length < 3) return '';

  return points.map((point, index) => {
    const command = index === 0 ? 'M' : 'L';
    return `${command} ${point.x} ${point.y}`;
  }).join(' ') + ' Z';
};

// Get the identifier used for selection (externalId matches property.id in the app)
const getShapeIdentifier = (shape) => shape.data?.externalId || shape.id;

// Normalize ID for matching (handles format differences like "MP01" vs "MP 03")
const normalizeId = (id) => {
  if (!id) return '';
  // Remove spaces and leading zeros from numbers
  return id.replace(/\s+/g, '').replace(/(\D)0+(\d)/g, '$1$2').toUpperCase();
};

// Get navigation target if shape has navigateTo
const getNavigationTarget = (shape) => shape.data?.navigateTo || null;

// Get color based on property status
const getStatusColor = (property) => {
  if (!property) return null;
  // status is an object with code property (from resolveStatus)
  const statusCode = property.status?.code || property.statusCode;
  return STATUS_COLORS[statusCode] || null;
};

// Individual shape region component
function ShapeRegion({
  shape,
  property,
  isSelected,
  isHovered,
  onClick,
  onHover,
  onNavigate,
  onTooltip,
  isTouchDevice,
  currentArtboard,
  defaultSoldForUnmatched,
  hoverOnlyColors
}) {
  const shapeId = getShapeIdentifier(shape);
  const navigateTo = getNavigationTarget(shape);

  const handleClick = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    if (shape.clickable !== false) {
      // If shape has navigateTo, handle navigation
      if (navigateTo && onNavigate) {
        onNavigate(navigateTo);
      } else {
        // Otherwise handle as property selection
        onClick(shapeId, event);
      }
    }
  }, [shape.clickable, shapeId, navigateTo, onClick, onNavigate]);

  const handleMouseEnter = useCallback((event) => {
    onHover(shapeId);
    if (onTooltip) {
      onTooltip(shapeId, event.nativeEvent);
    }
  }, [shapeId, onHover, onTooltip]);

  const handleMouseMove = useCallback((event) => {
    if (onTooltip) {
      onTooltip(shapeId, event.nativeEvent);
    }
  }, [shapeId, onTooltip]);

  const handleMouseLeave = useCallback(() => {
    onHover(null);
    if (onTooltip) {
      onTooltip(null, null);
    }
  }, [onHover, onTooltip]);

  const handleTouchStart = useCallback((event) => {
    event.preventDefault();
    onHover(shapeId);
    // Show tooltip on touch using touch coordinates
    if (onTooltip && event.touches && event.touches[0]) {
      const touch = event.touches[0];
      onTooltip(shapeId, { clientX: touch.clientX, clientY: touch.clientY });
    }
  }, [shapeId, onHover, onTooltip]);

  const handleTouchEnd = useCallback((event) => {
    event.preventDefault();
    if (shape.clickable !== false) {
      // If shape has navigateTo, handle navigation
      if (navigateTo && onNavigate) {
        onNavigate(navigateTo);
      } else {
        onClick(shapeId, event);
      }
    }
    // On mobile, don't auto-hide tooltip - user will close it manually
    // Only clear hover state
    onHover(null);
  }, [shape.clickable, shapeId, navigateTo, onClick, onNavigate, onHover]);

  if (!shape.points || shape.points.length < 3) {
    return null;
  }

  const svgPath = pointsToPath(shape.points);

  const statusColor = getStatusColor(property);
  const isAvailable = property?.status?.code === 'available';
  const isUnavailable = property && !isAvailable;
  const statusCode = property?.status?.code;

  let fillColor;
  let fillOpacity;
  let strokeColor;
  let strokeOpacity = 1;
  let strokeWidth = 2;
  let elementOpacity = 1;

  const isActive = isSelected || isHovered;

  if (property) {
    fillColor = statusColor;
    strokeColor = STATUS_BORDER_COLORS[statusCode] || statusColor;
    fillOpacity = isActive ? 0.55 : (hoverOnlyColors ? 0 : 0.3);
    strokeOpacity = hoverOnlyColors ? 0 : 1;
  } else {
    const isNavigationShape = !!navigateTo;
    const isNavigationArtboard = currentArtboard?.id?.includes('selector');
    const shouldDefaultToSold = defaultSoldForUnmatched && !isNavigationShape && !isNavigationArtboard;

    if (shouldDefaultToSold) {
      fillColor = STATUS_COLORS.sold;
      strokeColor = STATUS_BORDER_COLORS.sold;
      fillOpacity = isActive ? 0.55 : (hoverOnlyColors ? 0 : 0.3);
      strokeOpacity = hoverOnlyColors ? 0 : 1;
    } else if (isActive) {
      fillColor = DEFAULT_HOVER_FILL;
      fillOpacity = 0.5;
      strokeColor = DEFAULT_HOVER_FILL;
    } else {
      fillColor = DEFAULT_FILL;
      fillOpacity = DEFAULT_FILL_OPACITY;
      strokeColor = 'transparent';
    }
  }

  const isClickable = shape.clickable !== false && (!property || isAvailable);

  return (
    <g className="property-region-group">
      <path
        d={svgPath}
        className={`property-region ${isSelected ? 'selected' : ''} ${isHovered ? 'hovered' : ''} ${isUnavailable ? 'unavailable' : ''}`}
        fill={fillColor}
        fillOpacity={fillOpacity}
        stroke={strokeColor}
        strokeOpacity={strokeOpacity}
        strokeWidth={strokeWidth}
        style={{
          cursor: isClickable ? 'pointer' : 'not-allowed',
          pointerEvents: 'auto',
          opacity: elementOpacity
        }}
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      />
    </g>
  );
}

// Tooltip component for non-list items (parking, storage)
function MapTooltip({ property, position, containerRef, isMobile, onClose }) {
  // Refs and state for smart positioning (MUST be before any conditional returns)
  const tooltipRef = useRef(null);
  const [tooltipDimensions, setTooltipDimensions] = useState({ width: 0, height: 0 });
  const [calculatedPosition, setCalculatedPosition] = useState(null);

  // Measure tooltip dimensions after render
  useLayoutEffect(() => {
    if (tooltipRef.current && property) {
      const rect = tooltipRef.current.getBoundingClientRect();
      setTooltipDimensions({
        width: rect.width,
        height: rect.height
      });
    }
  }, [property]);

  // Calculate optimal position using smart algorithm
  useLayoutEffect(() => {
    if (!property || !position || !containerRef?.current) {
      setCalculatedPosition(null);
      return;
    }

    if (tooltipDimensions.width === 0) return;

    const rawRect = containerRef.current.getBoundingClientRect();
    const unscaledW = containerRef.current.offsetWidth || rawRect.width;
    const unscaledH = containerRef.current.offsetHeight || rawRect.height;
    const containerRect = {
      left: rawRect.left,
      top: rawRect.top,
      width: unscaledW,
      height: unscaledH
    };
    const offset = unscaledW < 400 ? 5 : 10;

    const bestPosition = selectBestPosition(
      position,
      tooltipDimensions,
      containerRect,
      offset
    );

    setCalculatedPosition(bestPosition);
  }, [property, position, containerRef, tooltipDimensions]);

  if (!property || !position) return null;

  const identifier = property.id || property.title;
  const rawArea = property.specifications?.area?.total;
  const rawPrice = property.pricing?.totalPrice?.value;
  const area = Number.isFinite(rawArea) ? rawArea : null;
  const price = Number.isFinite(rawPrice) ? rawPrice : null;
  const typeLabel = property.propertyTypeLabel || property.propertyType;

  const finalPosition = calculatedPosition || { x: position.x + 10, y: position.y - 10 };

  const tooltipStyle = {
    position: 'absolute',
    left: finalPosition.x,
    top: finalPosition.y,
    zIndex: 1000,
    pointerEvents: 'none'
  };

  return (
    <div ref={tooltipRef} className="map-tooltip" style={tooltipStyle}>
      <div style={{
        backgroundColor: '#1b1b1b',
        color: '#ffffff',
        border: '1px solid #374151',
        borderRadius: '6px',
        padding: '10px 14px',
        fontSize: '0.8125rem',
        fontFamily: "'Sora', sans-serif",
        fontWeight: 400,
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
        minWidth: '140px',
        maxWidth: '220px',
        position: 'relative'
      }}>
        {isMobile && onClose && (
          <button
            onClick={onClose}
            style={{
              position: 'absolute',
              top: '6px',
              right: '6px',
              background: 'transparent',
              border: 'none',
              color: '#9CA3AF',
              cursor: 'pointer',
              padding: '4px',
              fontSize: '1rem',
              lineHeight: 1,
              pointerEvents: 'auto'
            }}
          >
            ×
          </button>
        )}

        <div style={{
          fontWeight: 600,
          marginBottom: '6px',
          fontSize: '0.875rem',
          color: '#ffffff',
          paddingRight: isMobile ? '20px' : 0
        }}>
          {identifier}
        </div>

        {typeLabel && (
          <div style={{
            color: '#9CA3AF',
            fontSize: '0.75rem',
            marginBottom: '8px'
          }}>
            {typeLabel}
          </div>
        )}

        {area != null && (
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginBottom: '4px',
            color: '#D1D5DB'
          }}>
            <span>Metraż:</span>
            <span style={{ fontWeight: 500, color: '#ffffff' }}>{formatArea(area)}</span>
          </div>
        )}

        {price != null && (
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginBottom: area != null ? '4px' : 0,
            color: '#D1D5DB'
          }}>
            <span>Cena{property.propertyType === 'lokal uslugowy' ? ' netto' : ''}:</span>
            <span style={{ fontWeight: 500, color: '#ffffff' }}>{formatPLN(price)}</span>
          </div>
        )}

        {price != null && area != null && (
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            color: '#D1D5DB'
          }}>
            <span>Cena/m²{property.propertyType === 'lokal uslugowy' ? ' netto' : ''}:</span>
            <span style={{ fontWeight: 500, color: '#ffffff' }}>{formatPricePerM2(price, area)}</span>
          </div>
        )}
      </div>
    </div>
  );
}

// Rotation arc indicator — shows current 360° position
function RotationArc({ currentIdx, total }) {
  const size = 44;
  const cx = 22;
  const cy = 22;
  const r = 16;
  const rad = ((currentIdx / total) * 360 - 90) * Math.PI / 180;
  const dotX = cx + r * Math.cos(rad);
  const dotY = cy + r * Math.sin(rad);
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ display: 'block' }}>
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="rgba(255,255,255,0.25)" strokeWidth="2" />
      <circle cx={dotX} cy={dotY} r="3.5" fill="white" />
      <text x={cx} y={cy + 1} textAnchor="middle" dominantBaseline="middle" fontSize="7" fill="rgba(255,255,255,0.8)" fontFamily="'Sora', sans-serif" fontWeight="600">360°</text>
    </svg>
  );
}

// Main SVG overlay component for V2 format
const PropertySVGOverlayV2 = forwardRef(function PropertySVGOverlayV2({
  navigatorConfig,
  properties = [],
  selectedProperty,
  hoveredProperty,
  onPropertyClick,
  onPropertyHover,
  onArtboardChange,
  viewBox,
  enableNavigatorTooltips = false,
  onZoomIn,
  onZoomOut,
  canZoomIn = false,
  canZoomOut = false,
  overlayEnabled = true
}, ref) {
  const is360 = navigatorConfig?.is360 === true;
  const totalFrames = navigatorConfig?.artboards?.length || 1;

  // Preload all 360° frames so rotation never waits on network
  useEffect(() => {
    if (!is360 || !navigatorConfig?.artboards) return;
    navigatorConfig.artboards.forEach((ab) => {
      const url = ab.image?.url;
      if (url) {
        const img = new Image();
        img.src = url;
      }
    });
  }, [is360, navigatorConfig]);

  // Track current artboard ID (default to first artboard)
  const [currentArtboardId, setCurrentArtboardId] = useState(null);
  // Track navigation history for back button
  const [navigationHistory, setNavigationHistory] = useState([]);
  // 360° mode: track by index — persisted in sessionStorage so frame survives route changes
  const NAV_V3_FRAME_KEY = 'nav-v3-skorupki-1-frame';
  const [currentArtboardIdx, setCurrentArtboardIdx] = useState(() => {
    if (typeof window === 'undefined') return 0;
    const saved = window.sessionStorage.getItem(NAV_V3_FRAME_KEY);
    const parsed = saved !== null ? parseInt(saved, 10) : 0;
    return Number.isFinite(parsed) ? parsed : 0;
  });
  const currentArtboardIdxRef = useRef(0);
  useEffect(() => { currentArtboardIdxRef.current = currentArtboardIdx; }, [currentArtboardIdx]);
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.sessionStorage.setItem(NAV_V3_FRAME_KEY, String(currentArtboardIdx));
    }
  }, [currentArtboardIdx]);
  // Fade animation state for shapes (triggered by nav button clicks)
  const [shapesVisible, setShapesVisible] = useState(true);
  const fadeTimerRef = useRef(null);
  const rotateHoldRef = useRef(null);
  const rotateAnimRef = useRef(null);
  const tooltipDismissRef = useRef(null);
  const overlaySvgRef = useRef(null);
  // Drag state for 360° mode (refs to avoid re-renders)
  const dragRef = useRef({ active: false, startX: 0, lastX: 0, baseIdx: 0, velX: 0, inertiaRaf: null, rafDrag: null });
  // Tooltip state
  const [tooltipData, setTooltipData] = useState({ property: null, position: null });
  // Container ref for tooltip positioning
  const containerRef = useRef(null);
  // Stable refs to parent callbacks (avoid re-running drag useEffect)
  const onPropertyClickRef = useRef(onPropertyClick);
  const onPropertyHoverRef = useRef(onPropertyHover);
  useEffect(() => { onPropertyClickRef.current = onPropertyClick; }, [onPropertyClick]);
  useEffect(() => { onPropertyHoverRef.current = onPropertyHover; }, [onPropertyHover]);
  // Track if device is touch-based (mobile)
  const [isTouchDevice, setIsTouchDevice] = useState(false);

  // Detect touch-only device (no mouse hover)
  useEffect(() => {
    const checkTouch = () => {
      const noHover = window.matchMedia && window.matchMedia('(hover: none)').matches;
      const coarsePointer = window.matchMedia && window.matchMedia('(pointer: coarse)').matches;
      setIsTouchDevice(noHover && coarsePointer);
    };
    checkTouch();
  }, []);

  // 360° drag — mouse (document) + touch (container, passive:false)
  useEffect(() => {
    if (!is360) return;
    const drag = dragRef.current;
    const n = totalFrames;

    function cancelInertia() {
      if (drag.inertiaRaf) { cancelAnimationFrame(drag.inertiaRaf); drag.inertiaRaf = null; }
    }

    function showShapes() {
      setShapesVisible(true);
    }

    function startInertia() {
      if (Math.abs(drag.velX) < 2) {
        showShapes();
        return;
      }
      let v = drag.velX, acc = 0;
      function tick() {
        v *= 0.88; acc += v;
        if (Math.abs(acc) >= DRAG_PX_360) {
          const steps = Math.trunc(acc / DRAG_PX_360);
          acc -= steps * DRAG_PX_360;
          setShapesVisible(false);
          setCurrentArtboardIdx(prev => ((prev - steps) % n + n) % n);
        }
        if (Math.abs(v) > 0.5) {
          drag.inertiaRaf = requestAnimationFrame(tick);
        } else {
          drag.inertiaRaf = null;
          showShapes();
        }
      }
      drag.inertiaRaf = requestAnimationFrame(tick);
    }

    const onMouseMove = (e) => {
      if (!drag.active) return;
      drag.velX = e.clientX - drag.lastX;
      drag.lastX = e.clientX;
      if (drag.rafDrag) return;
      const capturedX = e.clientX;
      drag.rafDrag = requestAnimationFrame(() => {
        drag.rafDrag = null;
        const delta = Math.round((drag.startX - capturedX) / DRAG_PX_360);
        const newIdx = ((drag.baseIdx + delta) % n + n) % n;
        if (newIdx !== currentArtboardIdxRef.current) {
          setShapesVisible(false);
          setCurrentArtboardIdx(newIdx);
        }
      });
    };

    const onMouseUp = () => {
      if (!drag.active) return;
      drag.active = false;
      startInertia();
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);

    const el = containerRef.current;
    const onTouchStart = (e) => {
      cancelInertia();
      drag.active = true;
      drag.cleared = false;
      drag.startX = drag.lastX = e.touches[0].clientX;
      drag.baseIdx = currentArtboardIdxRef.current;
      drag.velX = 0;
      e.preventDefault();
    };
    const onTouchMove = (e) => {
      if (!drag.active) return;
      const x = e.touches[0].clientX;
      drag.velX = x - drag.lastX; drag.lastX = x;
      const movedPx = Math.abs(x - drag.startX);
      if (!drag.cleared && movedPx > 1) {
        drag.cleared = true;
        clearTimeout(tooltipDismissRef.current);
        setTooltipData({ property: null, position: null });
        if (onPropertyClickRef.current) onPropertyClickRef.current(null, null);
        if (onPropertyHoverRef.current) onPropertyHoverRef.current(null);
      }
      if (drag.rafDrag) return;
      const cx = x;
      drag.rafDrag = requestAnimationFrame(() => {
        drag.rafDrag = null;
        const delta = Math.round((drag.startX - cx) / DRAG_PX_360);
        const newIdx = ((drag.baseIdx + delta) % n + n) % n;
        if (newIdx !== currentArtboardIdxRef.current) {
          setShapesVisible(false);
          setCurrentArtboardIdx(newIdx);
        }
      });
      e.preventDefault();
    };
    const onTouchEnd = () => { drag.active = false; startInertia(); };

    if (el) {
      el.addEventListener('touchstart', onTouchStart, { passive: false });
      el.addEventListener('touchmove',  onTouchMove,  { passive: false });
      el.addEventListener('touchend',   onTouchEnd);
    }

    return () => {
      cancelInertia();
      if (drag.rafDrag) { cancelAnimationFrame(drag.rafDrag); drag.rafDrag = null; }
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup',   onMouseUp);
      if (el) {
        el.removeEventListener('touchstart', onTouchStart);
        el.removeEventListener('touchmove',  onTouchMove);
        el.removeEventListener('touchend',   onTouchEnd);
      }
    };
  }, [is360, totalFrames]);

  // Build property lookup map by ID for quick access (with normalized keys for flexible matching)
  const propertiesMap = useMemo(() => {
    const map = {};
    properties.forEach(prop => {
      if (prop.id) {
        // Store by original ID
        map[prop.id] = prop;
        // Also store by normalized ID for flexible matching (e.g., "MP 03" -> "MP3")
        const normalizedKey = normalizeId(prop.id);
        if (normalizedKey !== prop.id) {
          map[normalizedKey] = prop;
        }
      }
    });
    return map;
  }, [properties]);

  // Helper to find property by shape ID (tries normalized matching)
  const findPropertyByShapeId = (shapeId) => {
    // First try exact match
    if (propertiesMap[shapeId]) {
      return propertiesMap[shapeId];
    }
    // Then try normalized match
    const normalizedShapeId = normalizeId(shapeId);
    return propertiesMap[normalizedShapeId] || null;
  };

  // Build artboard lookup map
  const artboardsMap = useMemo(() => {
    const map = {};
    if (navigatorConfig?.artboards) {
      navigatorConfig.artboards.forEach(ab => {
        map[ab.id] = ab;
      });
    }
    return map;
  }, [navigatorConfig]);

  // Get first artboard ID
  const firstArtboardId = navigatorConfig?.artboards?.[0]?.id || null;

  // Get current artboard (360°: by index; standard: by ID)
  const currentArtboard = useMemo(() => {
    if (is360) return navigatorConfig?.artboards?.[currentArtboardIdx] || null;
    const targetId = currentArtboardId || firstArtboardId;
    return targetId ? artboardsMap[targetId] : null;
  }, [is360, currentArtboardIdx, currentArtboardId, firstArtboardId, artboardsMap, navigatorConfig]);

  const shapes = currentArtboard?.shapes || [];

  // Notify parent when artboard changes — useLayoutEffect so image swaps in same paint as shapes
  useLayoutEffect(() => {
    if (onArtboardChange && currentArtboard) {
      onArtboardChange(currentArtboard);
    }
  }, [currentArtboard, onArtboardChange]);

  // Handle navigation to another artboard
  const handleNavigate = useCallback((targetArtboardId) => {
    if (artboardsMap[targetArtboardId]) {
      // Save current artboard to history for back navigation
      const currentId = currentArtboardId || firstArtboardId;
      if (currentId) {
        setNavigationHistory(prev => [...prev, currentId]);
      }
      setCurrentArtboardId(targetArtboardId);
    }
  }, [artboardsMap, currentArtboardId, firstArtboardId]);

  // Handle back navigation
  const handleGoBack = useCallback(() => {
    if (navigationHistory.length > 0) {
      const previousId = navigationHistory[navigationHistory.length - 1];
      setNavigationHistory(prev => prev.slice(0, -1));
      setCurrentArtboardId(previousId);
    }
  }, [navigationHistory]);

  // Handle direct layer selection from dropdown
  const handleLayerSelect = useCallback((event) => {
    const targetId = event.target.value;
    if (targetId && artboardsMap[targetId]) {
      // Save current artboard to history
      const currentId = currentArtboardId || firstArtboardId;
      if (currentId && currentId !== targetId) {
        setNavigationHistory(prev => [...prev, currentId]);
      }
      setCurrentArtboardId(targetId);
    }
  }, [artboardsMap, currentArtboardId, firstArtboardId]);

  // Get all artboards for layer selector
  const allArtboards = useMemo(() => {
    return navigatorConfig?.artboards || [];
  }, [navigatorConfig]);

  // Close tooltip handler
  const handleCloseTooltip = useCallback(() => {
    clearTimeout(tooltipDismissRef.current);
    setTooltipData({ property: null, position: null });
  }, []);

  // Handle tooltip show/hide based on mouse position
  const handleTooltipUpdate = useCallback((shapeId, event, forceHide = false) => {
    // On mobile, don't auto-hide unless explicitly requested
    if (forceHide || (!shapeId || !event)) {
      if (!isTouchDevice || forceHide) {
        setTooltipData({ property: null, position: null });
      }
      return;
    }

    const property = findPropertyByShapeId(shapeId);

    // navigatorConfig.tooltipsOnMobile === false wyłącza dymki na dotyku
    // (brak pola = dotychczasowe zachowanie, dymki wszędzie)
    const tooltipsOnMobile = navigatorConfig?.tooltipsOnMobile !== false;
    const shouldShowTooltip = enableNavigatorTooltips && !!property
      && !(isTouchDevice && !tooltipsOnMobile);

    if (shouldShowTooltip && containerRef.current) {
      const containerRect = containerRef.current.getBoundingClientRect();
      const unscaledW = containerRef.current.offsetWidth || containerRect.width;
      const unscaledH = containerRef.current.offsetHeight || containerRect.height;
      const scaleX = containerRect.width / unscaledW;
      const scaleY = containerRect.height / unscaledH;
      const position = {
        x: (event.clientX - containerRect.left) / (scaleX || 1),
        y: (event.clientY - containerRect.top) / (scaleY || 1)
      };
      setTooltipData({ property, position });
      if (isTouchDevice) {
        clearTimeout(tooltipDismissRef.current);
        tooltipDismissRef.current = setTimeout(() => {
          setTooltipData({ property: null, position: null });
        }, 4000);
      }
    } else if (!isTouchDevice) {
      // On desktop, hide tooltip when hovering non-tooltip items
      setTooltipData({ property: null, position: null });
    }
  }, [findPropertyByShapeId, isTouchDevice, enableNavigatorTooltips, navigatorConfig]);

  // Use provided viewBox or get from current artboard
  const effectiveViewBox = viewBox || currentArtboard?.viewBox || '0 0 100 56.5';

  const handleMouseDown360 = useCallback((e) => {
    if (e.button !== 0) return;
    const drag = dragRef.current;
    if (drag.inertiaRaf) { cancelAnimationFrame(drag.inertiaRaf); drag.inertiaRaf = null; }
    drag.active = true;
    drag.startX = drag.lastX = e.clientX;
    drag.baseIdx = currentArtboardIdx;
    drag.velX = 0;
    setTooltipData({ property: null, position: null });
    if (onPropertyClick) onPropertyClick(null, null);
    if (onPropertyHover) onPropertyHover(null);
  }, [currentArtboardIdx, onPropertyClick, onPropertyHover]);

  const triggerShapesFade = useCallback(() => {
    setShapesVisible(false);
    clearTimeout(fadeTimerRef.current);
    fadeTimerRef.current = setTimeout(() => setShapesVisible(true), 280);
  }, []);

  const stepFrame = useCallback((dir) => {
    const drag = dragRef.current;
    if (drag.inertiaRaf) { cancelAnimationFrame(drag.inertiaRaf); drag.inertiaRaf = null; }
    setCurrentArtboardIdx(prev => ((prev + dir) % totalFrames + totalFrames) % totalFrames);
  }, [totalFrames]);

  const ROTATE_STEP = 6;
  const STEP_INTERVAL_MS = 28;
  const ROTATE_DURATION = ROTATE_STEP * STEP_INTERVAL_MS;
  const targetDeltaRef = useRef(0);
  const lastStepTimeRef = useRef(0);

  const cancelRotateAnim = useCallback(() => {
    if (rotateAnimRef.current) {
      cancelAnimationFrame(rotateAnimRef.current);
      rotateAnimRef.current = null;
    }
    targetDeltaRef.current = 0;
  }, []);

  const animateRotate = useCallback((dir) => {
    const n = totalFrames;
    if (!n) return;
    targetDeltaRef.current += dir * ROTATE_STEP;
    if (rotateAnimRef.current) return;
    setShapesVisible(false);
    lastStepTimeRef.current = performance.now() - STEP_INTERVAL_MS;
    const tick = (now) => {
      if (targetDeltaRef.current === 0) {
        rotateAnimRef.current = null;
        setShapesVisible(true);
        return;
      }
      if (now - lastStepTimeRef.current >= STEP_INTERVAL_MS) {
        const sign = Math.sign(targetDeltaRef.current);
        targetDeltaRef.current -= sign;
        setCurrentArtboardIdx(prev => ((prev + sign) % n + n) % n);
        lastStepTimeRef.current = now;
      }
      rotateAnimRef.current = requestAnimationFrame(tick);
    };
    rotateAnimRef.current = requestAnimationFrame(tick);
  }, [totalFrames]);

  const startRotateHold = useCallback((dir) => {
    animateRotate(dir);
    rotateHoldRef.current = setTimeout(function repeat() {
      animateRotate(dir);
      rotateHoldRef.current = setTimeout(repeat, ROTATE_DURATION);
    }, 250);
  }, [animateRotate]);

  const stopRotateHold = useCallback(() => {
    clearTimeout(rotateHoldRef.current);
    rotateHoldRef.current = null;
  }, []);

  const handleRotateLeft = useCallback(() => stepFrame(-ROTATE_STEP), [stepFrame]);
  const handleRotateRight = useCallback(() => stepFrame(ROTATE_STEP), [stepFrame]);

  // Circle arrows: 1-frame precision
  const handleArrowLeft = useCallback(() => stepFrame(-1), [stepFrame]);
  const handleArrowRight = useCallback(() => stepFrame(1), [stepFrame]);
  const startArrowHold = useCallback((dir) => {
    stepFrame(dir);
    rotateHoldRef.current = setTimeout(function repeat() {
      stepFrame(dir);
      rotateHoldRef.current = setTimeout(repeat, 80);
    }, 350);
  }, [stepFrame]);

  useImperativeHandle(ref, () => ({
    rotateOnce: animateRotate,
  }), [animateRotate]);

  if (!is360 && shapes.length === 0) {
    return null;
  }

  const showBackButton = !is360 && navigationHistory.length > 0;
  const showDropdown = !is360 && allArtboards.length > 1;
  const currentArtboardIdForSelect = currentArtboardId || firstArtboardId;

  const showNavigationControls = showBackButton || showDropdown;

  return (
    <div
      ref={containerRef}
      style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', cursor: is360 ? 'grab' : undefined }}
      onMouseDown={is360 ? handleMouseDown360 : undefined}
    >
      {/* Navigation controls - only visible when needed (non-360 mode) */}
      {showNavigationControls && (
        <div className="navigator-controls">
          {showBackButton && (
            <button className="nav-back-btn" onClick={handleGoBack}>
              ← Wróć
            </button>
          )}
          {showDropdown && (
            <select
              className="nav-layer-select"
              value={currentArtboardIdForSelect}
              onChange={handleLayerSelect}
            >
              {allArtboards.map(artboard => (
                <option key={artboard.id} value={artboard.id}>
                  {artboard.name || artboard.id}
                </option>
              ))}
            </select>
          )}
        </div>
      )}

      {/* SVG shapes overlay — ALWAYS mounted to keep ref + visibility stable across empty-shape frames */}
      <svg
          ref={overlaySvgRef}
          className="property-overlay"
          viewBox={effectiveViewBox}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            pointerEvents: 'none',
            opacity: shapesVisible && overlayEnabled ? 1 : 0,
            transition: shapesVisible ? 'opacity 0.18s ease' : 'opacity 0s'
          }}
          preserveAspectRatio="xMidYMid meet"
        >
          <defs>
            <pattern id="grid" width="50" height="50" patternUnits="userSpaceOnUse">
              <path d="M 50 0 L 0 0 0 50" fill="none" stroke="#f0f0f0" strokeWidth="1" opacity="0.1"/>
            </pattern>
          </defs>

          {shapes.map(shape => {
            const shapeId = getShapeIdentifier(shape);
            const property = findPropertyByShapeId(shapeId);
            return (
              <ShapeRegion
                key={shapeId}
                shape={shape}
                property={property}
                isSelected={selectedProperty === shapeId}
                isHovered={hoveredProperty === shapeId}
                onClick={onPropertyClick}
                onHover={onPropertyHover}
                onNavigate={handleNavigate}
                onTooltip={handleTooltipUpdate}
                isTouchDevice={isTouchDevice}
                currentArtboard={currentArtboard}
                defaultSoldForUnmatched={navigatorConfig?.defaultSoldForUnmatched}
                hoverOnlyColors={navigatorConfig?.hoverOnlyColors}
              />
            );
          })}
        </svg>

      {/* Tooltip */}
      <MapTooltip
        property={tooltipData.property}
        position={tooltipData.position}
        containerRef={containerRef}
        isMobile={isTouchDevice}
      />
    </div>
  );
});

export default PropertySVGOverlayV2;
